import { useState, useMemo, useEffect, useRef } from 'react';
import { BookOpen, ChevronRight, FileText, Layers, Database, Server, Search } from 'lucide-react';

/**
 * Platform Documentation — read-only mapping of:
 *   Page → Components → MongoDB Collections → MySQL Source → Notes
 *
 * Sections are flat data; rendering is driven by `SECTIONS`. Add new modules by
 * appending here. No backend calls; entirely static.
 */

const SECTIONS = [
  // ─────────────────────── INTAKE HUB — MISSING BILLS ───────────────────────
  {
    id: 'intake-missing-bills',
    title: 'Intake Hub — Missing Bills (Module 7)',
    icon: Layers,
    summary: 'Forecasts recurring bills per vendor-account and flags Late Arriving / Missing periods. Includes the multifamily occupancy use case.',
    pages: [
      {
        id: 'missing-bills-page',
        route: '/intake-hub/missing-bills',
        component: 'frontend/src/pages/intake/MissingBills.jsx',
        purpose: 'Two tabs (Late Arriving / Missing). One row PER missing billing period, each with its own expected date + days-overdue. Global filters (Property, Vendor, Account #, Severity), assignment, append-only notes, snooze (7d), mark-received, bill-PDF drawer (slides from left), CSV.',
        api: [
          { method: 'POST', path: '/api/intake/missing-bills/recompute' },
          { method: 'GET', path: '/api/intake/missing-bills/list' },
          { method: 'GET', path: '/api/intake/missing-bills/filter-options' },
          { method: 'PATCH', path: '/api/intake/missing-bills/account-settings' },
          { method: 'GET', path: '/api/intake/missing-bills/export.csv' },
          { method: 'PATCH', path: '/api/intake/missing-bills/{id}/assign' },
          { method: 'POST', path: '/api/intake/missing-bills/{id}/snooze | mark-received | notes' },
          { method: 'GET', path: '/api/super-admin/intake/missing-bills/job-status (Cockpit)' },
          { method: 'POST', path: '/api/super-admin/intake/missing-bills/run-now (Cockpit)' },
        ],
        mongo: [
          { col: 'vendor_account_bill_forecast_active', use: 'PRIMARY — one row per missing billing period (account_id, vendor, account#, expected_issue_date unique).' },
          { col: 'vendor_account_bill_forecast_archive', use: 'Received + auto-ignored rows (keeps active list small/fast).' },
          { col: 'missing_bill_notes', use: 'Append-only notes thread per forecast row.' },
          { col: 'missing_bill_flagger_runs', use: 'Daily flagger run log (Cockpit → Job Queue panel).' },
          { col: 'bills', use: 'Source of issue dates for cadence inference (grouped by vendor + account#).' },
          { col: 'ubm_vendor_accounts', use: 'Eligibility — common-area accounts with status=active. Holds frequency + missing_bills_expected_start_date.' },
          { col: 'ubm_unit_accounts', use: 'Eligibility — vacant/auto-occupied units. Auto-flipped to occupied + holds expected_start_date.' },
          { col: 'org_workflow_config', use: 'GATE — flagger only runs for orgs with a config doc (deliberate cutover).' },
        ],
        notes: [
          'CADENCE: driven by the account\'s declared frequency (Monthly=30d, Quarterly=91d) else inferred from prior bills. Irregular spend → "need-based" (never auto-flagged Missing).',
          'PER-PERIOD ROWS: a monthly account whose last bill was 3 months ago shows 3 rows (one per missed month). Newest crossed period = late_arriving; older = missing. Severity: amber > 3d, red > 4d overdue.',
          'MULTIFAMILY USE CASE (units): when a vacant unit (landlord-billed) stops receiving bills, a tenant likely moved in. If NO bill arrives within 45 days of an expected period, the unit is auto-flipped to OCCUPIED. Once occupied and the series is 90+ days stale, the whole missing-bill series is auto-marked "Ignore", archived, and stamped with an auto-note "Looks like the unit has been occupied." Common-area vendor accounts are NOT auto-ignored — they always need chasing.',
          'ELIGIBILITY: active accounts only, requiring ≥1 past bill OR a Missing-Bills Expected Start Date (set in Vendor/Unit Account Add/Edit; backfilled from the oldest bill on platform).',
          'WHEN A BILL ARRIVES: the periods it covers drop out of the regenerated series and are archived as "received".',
          'SuperAdmin read-only rollup: /super-admin/intake-rollup. Daily job monitored in Cockpit → Job Queue.',
        ],
      },
    ],
  },

  // ───────────────────────── ORG BILLS MODULE ─────────────────────────
  {
    id: 'org-bills',
    title: 'Org Bills Module',
    icon: FileText,
    summary: 'Org-scoped bill workflows. Browses, filters, opens, and pays/refunds parsed bills.',
    pages: [
      {
        id: 'bills-list',
        route: '/bills',
        component: 'frontend/src/pages/bills/BillsPage.js',
        purpose: 'Main bill browser with filters, pagination, pay/refund actions.',
        api: [
          { method: 'GET', path: '/api/bills/filters' },
          { method: 'GET', path: '/api/bills/stats' },
          { method: 'GET', path: '/api/bills/list' },
          { method: 'GET', path: '/api/payments/v2/workspace/bill-payment-summary' },
          { method: 'GET', path: '/api/payments/v2/files/{pf_id}/utilities' },
          { method: 'POST', path: '/api/payments/v2/files/{pf_id}/record-payment' },
          { method: 'POST', path: '/api/payments/v2/files/{pf_id}/record-refund' },
          { method: 'POST', path: '/api/bills/sync/trigger' },
        ],
        mongo: [
          { col: 'bills', use: 'PRIMARY — list, filter, stats. Stores parsed bills (charges, identifiers, property linkage).' },
          { col: 'pm_payment_file_bills', use: 'JOINED for live paid/refund status overrides on bills attached to a PF.' },
          { col: 'pm_payment_file_utilities', use: 'JOINED to fetch utility row when recording payment/refund (account_number, site_id).' },
          { col: 'ubm_locations', use: 'Property denormalisation source for `bills.linked_location_id`.' },
          { col: 'ubm_unit_accounts / ubm_utility_accounts', use: 'Account → location resolution at sync time.' },
        ],
        mysql: [
          { tbl: 'parserdata', use: 'Source of `bills` — WHERE BillTrackId IS NOT NULL AND IsReviewed=\'YES\'.' },
          { tbl: 'parseridentifiersdata', use: 'Sub-meters / service addresses joined via BillId.' },
          { tbl: 'parserchargesdata', use: 'Line-item charges joined via IdentifierId.' },
        ],
        notes: [
          'Currently the `bills` collection is stale; sync last ran 2026-04-17. ~3,000 bills in MySQL after this date are missing here.',
          '`linked_location_id` stores `ubm_locations.id` (Mongo internal UUID), NOT `bp_property_id`.',
          'Pay/refund actions hit `pm_payment_file_bills` not `bills` — `bills` is read-mostly except for sync writes.',
        ],
      },
      {
        id: 'bills-detail',
        route: '/bills/:billId',
        component: 'frontend/src/pages/bills/BillViewPage.js',
        purpose: 'Single-bill detail view: header, line items, related bills, PDF preview, manual payment.',
        api: [
          { method: 'GET', path: '/api/bills/{billId}' },
          { method: 'GET', path: '/api/bills/{billId}/charges-summary' },
          { method: 'GET', path: '/api/bills/{billId}/related' },
          { method: 'GET', path: '/api/payments/v2/bill-pdf/{billId}' },
          { method: 'POST', path: '/api/bills/record-payment' },
        ],
        mongo: [
          { col: 'bills', use: 'PRIMARY — single doc lookup by `bill_id` and `account_id`.' },
          { col: 'pm_payment_file_bills', use: 'For live paid/refund overlay if bill attached to a PF.' },
          { col: 'pm_payment_files', use: 'PDF source: PF artefact stored under PF id.' },
        ],
        mysql: [],
        notes: [
          '"Related bills" matches by account_number + customer_id within the same org.',
          'Bill PDFs are streamed from S3 via the `/payments/v2/bill-pdf` proxy (no Mongo binary blob).',
        ],
      },
      {
        id: 'bills-dashboard',
        route: '/bills/dashboard',
        component: 'frontend/src/pages/bills/BillsDashboardPage.js',
        purpose: 'Spend summary, monthly-spend chart, top vendors, spend-by-property, recent bills.',
        api: [
          { method: 'GET', path: '/api/bills/dashboard/summary' },
          { method: 'GET', path: '/api/bills/dashboard/monthly-spend' },
          { method: 'GET', path: '/api/bills/dashboard/top-vendors' },
          { method: 'GET', path: '/api/bills/dashboard/spend-by-property' },
          { method: 'GET', path: '/api/bills/dashboard/recent-bills-by-property' },
          { method: 'GET', path: '/api/bills/dashboard/filters' },
        ],
        mongo: [
          { col: 'bills', use: 'ALL aggregates source from here — total_amount_due / vendor_name / linked_location_id.' },
          { col: 'ubm_locations', use: 'Property name / address enrichment in spend-by-property.' },
        ],
        mysql: [],
        notes: [
          'This dashboard reads `bills` only — staleness in `bills` shows up here as missing recent invoices.',
          'Distinct from the new Org Dashboard at `/dashboard`, which sources from `pm_payment_file_bills` for PF-based spend.',
        ],
      },
    ],
  },

  // ───────────────────────── ORG PAYMENTS MODULE ─────────────────────────
  {
    id: 'org-payments',
    title: 'Org Payments Module',
    icon: Layers,
    summary: 'Payment-file lifecycle: dashboard tiles, PF generator, workspace, reports, emergency uploads.',
    pages: [
      {
        id: 'payments-dashboard',
        route: '/payments/dashboard',
        component: 'frontend/src/pages/payments/PaymentsDashboard.js',
        purpose: 'Operational tiles — In-PF / Not-yet-batched, due-buckets, status distribution, refund totals.',
        api: [
          { method: 'GET', path: '/api/payments/v2/workspace/dashboard' },
        ],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'Counts In-PF bills, status distribution, due-date aging, refund totals.' },
          { col: 'pm_bill_statements', use: '"Not yet batched" tile — count where `is_in_payment_file=false`.' },
          { col: 'pm_payment_files', use: 'PF status distribution (review / approved / paid).' },
        ],
        mysql: [],
        notes: [
          'Single backend endpoint computes 20+ KPIs in parallel (`asyncio.gather`).',
          '"Not yet batched" is the only tile that reads `pm_bill_statements`; everything else reads `pm_payment_file_bills`.',
        ],
      },
      {
        id: 'payments-files',
        route: '/payments',
        component: 'frontend/src/pages/payments/PaymentsV2Page.js',
        purpose: 'PF generator — list/create payment files, batch payment stats, bulk upload transactions, CSV export.',
        api: [
          { method: 'GET', path: '/api/payments/v2/files' },
          { method: 'GET', path: '/api/payments/v2/files/batch-issue-stats' },
          { method: 'GET', path: '/api/payments/v2/files/batch-payment-stats' },
          { method: 'GET', path: '/api/payments/v2/stats' },
          { method: 'POST', path: '/api/payments/v2/files/{pf_id}/resync' },
          { method: 'GET', path: '/api/payments/v2/files/{pf_id}/export-csv' },
          { method: 'POST', path: '/api/payments/v2/bulk-upload-transactions/preview' },
          { method: 'POST', path: '/api/payments/v2/bulk-upload-transactions/commit' },
          { method: 'GET', path: '/api/payments/v2/bulk-upload-transactions/template' },
        ],
        mongo: [
          { col: 'pm_payment_files', use: 'PRIMARY — list of PFs with status, batch_name, totals.' },
          { col: 'pm_payment_file_bills', use: 'Per-PF bill counts and amounts.' },
          { col: 'pm_payment_file_transactions', use: 'Transaction stats per PF for stats endpoints.' },
          { col: 'pm_payment_file_utilities', use: 'Per-bill utility lines used during PF resync.' },
        ],
        mysql: [
          { tbl: 'paymentfile', use: 'Source of `pm_payment_files` — synced via payments_v2/sync_service.py.' },
          { tbl: 'paymentfilebills', use: 'Source of `pm_payment_file_bills`.' },
          { tbl: 'paymentfileutilities', use: 'Source of `pm_payment_file_utilities` — supplies SiteId for property linkage.' },
          { tbl: 'paymentfilepaths', use: 'PF download artefacts — embedded into `pm_payment_files`.' },
          { tbl: 'transactiondetails', use: 'Embedded transactions for bills.' },
          { tbl: 'paymenthistory', use: 'Source of `pm_payment_history`.' },
        ],
        notes: [
          'Resync flow re-pulls a single PF from MySQL and overwrites Mongo state.',
          'Bulk upload transactions writes directly into `pm_payment_file_bills` and `pm_payment_file_transactions`.',
        ],
      },
      {
        id: 'payments-pf-detail',
        route: '/payments/files/:pfId  (modal/sheet inside PaymentsV2Page)',
        component: 'frontend/src/pages/payments/PaymentFileDetail.js',
        purpose: 'Drill-down on one PF: bills, audit log, duplicates, issue summary, funds-pulled, LLC options, ACH file generation.',
        api: [
          { method: 'GET', path: '/api/payments/v2/files/{pfId}' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/bills' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/audit-log' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/utilities' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/duplicates' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/issue-summary' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/funds-pulled' },
          { method: 'GET', path: '/api/payments/v2/files/{pfId}/llc-options' },
          { method: 'POST', path: '/api/payments/v2/files/{pfId}/funds-pulled' },
        ],
        mongo: [
          { col: 'pm_payment_files', use: 'PF header and totals.' },
          { col: 'pm_payment_file_bills', use: 'Bills attached to this PF.' },
          { col: 'pm_payment_file_utilities', use: 'Utility-row detail per bill.' },
          { col: 'pm_payment_file_transactions', use: 'Transactions per bill.' },
          { col: 'pv2_payment_audit', use: 'Audit log entries for this PF.' },
          { col: 'accounts', use: 'LLC options (sender bank accounts) for ACH file generation.' },
        ],
        mysql: [],
        notes: [
          'Audit log is Mongo-only; no MySQL counterpart.',
          'Duplicate detector and issue-summary aggregate locally on `pm_payment_file_bills`.',
        ],
      },
      {
        id: 'payments-workspace',
        route: '/payments/bills',
        component: 'frontend/src/pages/payments/PaymentsBillsWorkspace.js (isGlobal=false)',
        purpose: 'PF Bills workspace — search, lock-bill, record/clear payment, refund flow, transactions log.',
        api: [
          { method: 'GET', path: '/api/payments/v2/workspace/filter-options' },
          { method: 'GET', path: '/api/payments/v2/workspace/bills' },
          { method: 'GET', path: '/api/payments/v2/workspace/bill-locks' },
          { method: 'POST', path: '/api/payments/v2/workspace/lock-bill' },
          { method: 'POST', path: '/api/payments/v2/workspace/unlock-bill' },
          { method: 'GET', path: '/api/payments/v2/workspace/bill-payment-summary' },
          { method: 'GET', path: '/api/payments/v2/workspace/bills/export (CSV)' },
          { method: 'GET', path: '/api/payments/v2/bill-transactions/{billId}' },
          { method: 'POST', path: '/api/payments/v2/files/{pf}/record-payment' },
          { method: 'POST', path: '/api/payments/v2/files/{pf}/record-refund' },
          { method: 'POST', path: '/api/payments/v2/files/{pf}/clear-payment' },
          { method: 'POST', path: '/api/payments/v2/files/{pf}/clear-refund' },
          { method: 'DELETE', path: '/api/payments/v2/delete-transaction/{txnId}' },
        ],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'PRIMARY — every list/filter/aggregation queries this. All `_dn_*` filters live here.' },
          { col: 'pm_payment_file_utilities', use: 'Utility detail and refund/payment context.' },
          { col: 'pm_payment_file_transactions', use: 'Per-bill transactions log.' },
          { col: 'pm_bill_locks', use: 'Optimistic-lock entries to prevent concurrent edits.' },
          { col: 'pm_payment_files', use: 'PF status overlay for filtering by review/approved/paid.' },
        ],
        mysql: [],
        notes: [
          'Filters use `_dn_*` denormalised fields: `_dn_supplier_name`, `_dn_property_state`, `_dn_utility_type`, `_dn_linked_location_id`.',
          'Bill locks are Mongo-only and TTL-expire; not synced from MySQL.',
        ],
      },
      {
        id: 'payments-service-fees',
        route: '/payments/service-fees',
        component: 'frontend/src/pages/payments/ServiceFeeReport.js',
        purpose: 'Service-fee revenue rollup by property and by vendor.',
        api: [
          { method: 'GET', path: '/api/payments/v2/service-fees/by-property' },
          { method: 'GET', path: '/api/payments/v2/service-fees/by-vendor' },
        ],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'PRIMARY — aggregates `payment_service_charge` by property/vendor.' },
          { col: 'pm_payment_files', use: '$lookup for PF status filtering (only paid PFs typically).' },
        ],
        mysql: [],
        notes: [
          '"Service charge" = `payment_service_charge` field; populated when a PF is paid.',
        ],
      },
      {
        id: 'payments-variance',
        route: '/payments/variance',
        component: 'frontend/src/pages/payments/VarianceReport.js',
        purpose: 'Variance between requested vs paid amounts; surfaces under/overpayments.',
        api: [
          { method: 'GET', path: '/api/payments/v2/variance/by-property' },
          { method: 'GET', path: '/api/payments/v2/variance/by-vendor' },
        ],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'PRIMARY — diffs `payment_amount` vs `paid_amount` per group.' },
          { col: 'pm_payment_files', use: '$lookup to filter only paid PFs.' },
        ],
        mysql: [],
        notes: [
          'Property grouping uses `_dn_linked_location_id` (Mongo internal UUID).',
        ],
      },
      {
        id: 'payments-credit-debit',
        route: '/payments/credit-debit',
        component: 'frontend/src/pages/payments/CreditDebitReportPage.js',
        purpose: 'Credit / debit reconciliation against uploaded bank statements.',
        api: [
          { method: 'GET', path: '/api/payments/v2/credit-debit/report' },
          { method: 'GET', path: '/api/payments/v2/credit-debit/uploads' },
          { method: 'POST', path: '/api/payments/v2/credit-debit/upload' },
        ],
        mongo: [
          { col: 'pm_bank_statement_uploads', use: 'Uploaded bank-statement files (one row per upload).' },
          { col: 'pm_bank_statement_lines', use: 'Parsed lines from each upload — matched against PF transactions.' },
          { col: 'pm_payment_file_transactions', use: 'PF-side transactions to match against bank lines.' },
          { col: 'pm_payment_file_bills', use: 'Bills surface in unmatched/credit-debit detail rows.' },
        ],
        mysql: [],
        notes: [
          'Bank statement parsing is Python-side; no MySQL involvement.',
          'Upload uses chunked transfer to bypass proxy limits.',
        ],
      },
      {
        id: 'payments-emergency',
        route: '/payments/emergency-bill-upload',
        component: 'frontend/src/pages/payments/EmergencyBillUpload.js',
        purpose: 'Manual upload path for paid bills outside the standard parser flow.',
        api: [
          { method: 'POST', path: '/api/payments/v2/emergency-bills/upload' },
          { method: 'GET', path: '/api/payments/v2/emergency-bills' },
        ],
        mongo: [
          { col: 'pm_emergency_bill_uploads', use: 'Stores manual one-off paid-bill submissions awaiting reconciliation.' },
          { col: 'emergency_payments', use: 'Payment side of emergency uploads (linked to a PF or standalone).' },
        ],
        mysql: [],
        notes: [
          'Standalone flow — does NOT touch parserdata or paymentfile tables in MySQL.',
        ],
      },
    ],
  },

  // ───────────────────────── ORG DASHBOARD ─────────────────────────
  {
    id: 'org-dashboard',
    title: 'Org Dashboard',
    icon: Server,
    summary: 'Top-level org dashboard at /dashboard. Grafana-style layout: KPIs, monthly spend, map, spend comparison, commodity tiles.',
    pages: [
      {
        id: 'org-dashboard-page',
        route: '/dashboard',
        component: 'frontend/src/pages/dashboard/OrgDashboardPage.js',
        purpose: 'Main consolidated org dashboard.',
        api: [
          { method: 'GET', path: '/api/dashboard/org/link-status' },
          { method: 'GET', path: '/api/dashboard/org/summary' },
          { method: 'GET', path: '/api/dashboard/org/spend-comparison (Postgres analytics fallback)' },
          { method: 'GET', path: '/api/dashboard/org/mongo/filter-options' },
          { method: 'GET', path: '/api/dashboard/org/mongo/kpis' },
          { method: 'GET', path: '/api/dashboard/org/mongo/monthly-spend' },
          { method: 'GET', path: '/api/dashboard/org/mongo/spend-comparison' },
        ],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'PRIMARY for KPIs, monthly-spend, spend-comparison. Aggregates `payment_amount` by `_dn_*` fields.' },
          { col: 'ubm_locations', use: 'Property names, addresses, lat/lng for the Map widget.' },
          { col: 'ubm_utility_accounts', use: 'Account-number → location fallback when `_dn_linked_location_id` is null.' },
          { col: 'ubm_unit_accounts', use: 'Unit-level account fallback.' },
          { col: 'accounts', use: 'Org metadata + cross-org guard (`bp_org_id`).' },
        ],
        mysql: [
          { tbl: 'entity / botaccounts', use: 'Read-only fallback in spend-comparison when an account_number isn\'t in Mongo UBM.' },
        ],
        notes: [
          'Spend-comparison reconciles with the Total Spend KPI — both source from `payment_amount` on `pm_payment_file_bills`.',
          'Property linkage is THREE-tier: persisted `_dn_linked_location_id` → acc→loc map → fuzzy address match.',
          'Unmapped bills are NOT lumped into "Unallocated" — they\'re grouped by service_address so users see what needs onboarding.',
          'Cross-org guard: lookups use composite `(bp_property_id, account_id)` to prevent UAT clones leaking.',
          '`/api/dashboard/org/spend-comparison` (no `mongo`) is the legacy Postgres fallback; the page calls both and prefers Mongo.',
        ],
      },
      {
        id: 'org-dashboard-widgets',
        route: '— (sub-components rendered inside OrgDashboardPage)',
        component: 'OrgDashboardPage internal widgets (no separate files yet)',
        purpose: 'Map / KPI tiles / Commodity hero cards / Filter bar / Spend Comparison table.',
        api: [],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'KPI tiles, Spend Comparison table, monthly trend.' },
          { col: 'ubm_locations', use: 'Map markers (filtered by `account_id`, `status=active`).' },
          { col: 'bills', use: 'Filter options (states, suppliers) sourced from here for performance.' },
        ],
        mysql: [],
        notes: [
          'No widget queries `bills` for spend — only for the dropdown filter values.',
          'Commodity tiles bucket `_dn_utility_type` into electricity / gas / water / sewer / trash / other.',
        ],
      },
    ],
  },

  // ───────────────────────── SUPER ADMIN PAYMENTS ─────────────────────────
  {
    id: 'admin-payments',
    title: 'Super Admin Payments',
    icon: Database,
    summary: 'Platform-wide views (all orgs) for ops staff. Lives inside the Super Admin shell + Global Workspace route.',
    pages: [
      {
        id: 'admin-dashboard',
        route: '/super-admin (activeTab=\'dashboard\')',
        component: 'frontend/src/pages/SuperAdmin.js — Dashboard tab block',
        purpose: 'Platform Dashboard — bill processing across all orgs, supplier analytics, time-range filtered.',
        api: [
          { method: 'GET', path: '/api/payment-verification/v2/supplier-analytics' },
          { method: 'GET', path: '/api/payment-verification/v2/supplier-analytics/csv' },
          { method: 'GET', path: '/api/super-admin/dashboard-stats' },
          { method: 'GET', path: '/api/super-admin/bp-partners' },
        ],
        mongo: [
          { col: 'bills', use: 'Bill processing volume, parser stats across all orgs.' },
          { col: 'pm_payment_file_bills', use: 'Cross-org PF metrics.' },
          { col: 'accounts', use: 'Org enumeration and partner filter.' },
        ],
        mysql: [
          { tbl: 'parserdata + parseridentifiersdata', use: 'Live supplier-analytics aggregations (live MySQL — not synced to Mongo first).' },
        ],
        notes: [
          'Supplier Analytics reads MySQL directly via `payment_verification_v2/routes.py` (faster, always fresh).',
          'Top-20 supplier-by-volume is org-aware: counts unique account_numbers per supplier across orgs.',
        ],
      },
      {
        id: 'admin-payment-files',
        route: '/super-admin (activeTab=\'payment-files\')',
        component: 'frontend/src/pages/SuperAdmin.js — Payment Files tab block + SuperAdminByCustomerCard',
        purpose: 'All payment files across orgs with filters, status, totals; per-customer grouped card.',
        api: [
          { method: 'GET', path: '/api/payments/v2/files (account_id omitted = all)' },
          { method: 'GET', path: '/api/cockpit/account-snapshot' },
          { method: 'GET', path: '/api/cockpit/account-snapshot/orgs' },
          { method: 'POST', path: '/api/cockpit/account-snapshot/rebuild' },
        ],
        mongo: [
          { col: 'pm_payment_files', use: 'Cross-org PF list.' },
          { col: 'pm_payment_file_bills', use: 'Per-PF bill counts.' },
          { col: 'account_snapshots', use: 'Pre-computed per-account rollup served by SuperAdminByCustomerCard for performance.' },
        ],
        mysql: [],
        notes: [
          '`account_snapshots` is a derived collection rebuilt on demand (cockpit/account-snapshot/rebuild).',
          'SuperAdmin filters by partner_id which maps to MySQL `partner.Id` → `customer.PartnerId` chain.',
        ],
      },
      {
        id: 'admin-global-workspace',
        route: '/payments/workspace',
        component: 'frontend/src/pages/payments/PaymentsBillsWorkspace.js (isGlobal=true)',
        purpose: 'Cross-org PF Bills workspace for ops staff. Same UI as org workspace but spans all non-excluded accounts.',
        api: [
          { method: 'GET', path: '/api/payments/v2/workspace/bills (account_id omitted)' },
          { method: 'GET', path: '/api/payments/v2/workspace/dashboard (global)' },
          { method: 'GET', path: '/api/payments/v2/workspace/filter-options' },
          { method: 'GET', path: '/api/payments/v2/workspace/bills/export' },
          { method: 'POST', path: '/api/payments/v2/workspace/lock-bill / unlock-bill' },
          { method: 'GET', path: '/api/accounts/{id}/users/{uid}/feature-visibility' },
          { method: 'GET', path: '/api/accounts/{id}/users-list' },
        ],
        mongo: [
          { col: 'pm_payment_file_bills', use: 'PRIMARY — query without `account_id` filter (excludes `record_status=removed`).' },
          { col: 'pm_payment_file_utilities', use: 'Utility detail.' },
          { col: 'pm_payment_file_transactions', use: 'Transactions log.' },
          { col: 'pm_bill_locks', use: 'Lock state.' },
          { col: 'accounts', use: 'Excludes `sync_excluded=true` orgs (via `inactive_account_ids` filter).' },
          { col: 'pm_bill_statements', use: '"Not yet batched" tile only — count where `is_in_payment_file=false`.' },
        ],
        mysql: [],
        notes: [
          'Cross-org leak prevention: queries filter `account_id $nin <sync_excluded ids>` AND `record_status != "removed"`.',
          'CSV export honours every active filter; first column is `bill_id` (UUID) — proposed change to `mysql_bill_id` is pending.',
          'Permission scoping per user (`feature-visibility`) is currently not enforced server-side for global tabs (tracked as P0 issue).',
        ],
      },
    ],
  },

  // ─────────────────────── ORG DASHBOARD MODULE ───────────────────────
  {
    id: 'org-dashboard',
    title: 'Org Dashboard Module',
    icon: Layers,
    summary: 'Org-scoped Grafana-style dashboard: KPIs, Map, Spend Comparison, Monthly Spend, and per-commodity usage charts. Fully Mongo-backed.',
    pages: [
      {
        id: 'org-dashboard-main',
        route: '/dashboard',
        component: 'frontend/src/pages/dashboard/OrgDashboardPage.js',
        purpose: 'Single-page Org Dashboard rendering 5 widgets driven by a shared filter (date range, state, supplier, site).',
        api: [
          { method: 'GET', path: '/api/dashboard/org/link-status' },
          { method: 'GET', path: '/api/dashboard/org/mongo/filter-options' },
          { method: 'GET', path: '/api/dashboard/org/mongo/kpis' },
          { method: 'GET', path: '/api/dashboard/org/mongo/commodity-usage' },
          { method: 'GET', path: '/api/dashboard/org/mongo/commodity-usage-export' },
          { method: 'GET', path: '/api/dashboard/org/mongo/monthly-spend' },
          { method: 'GET', path: '/api/dashboard/org/mongo/monthly-spend-export' },
          { method: 'GET', path: '/api/dashboard/org/mongo/spend-comparison' },
          { method: 'POST', path: '/api/dashboard/org/mongo/backfill-usage' },
        ],
        mongo: [
          { col: 'bills', use: 'PRIMARY — every widget. Filtered on `account_id`, `is_cross_customer != true`, `invoice_date` range. Source of `current_bill_amount`, `total_amount_due`, and `_dn_usage_*` denorm fields.' },
          { col: 'pm_payment_file_bills', use: 'KPI sub-stats only — Requested (`payment_amount`) and Paid (`paid_amount`).' },
          { col: 'pm_bank_statement_lines', use: 'KPI Pulled stat — sum of `credit` for line_type="credit", status != "deleted".' },
          { col: 'pm_funds_pulled', use: 'KPI Pulled stat — legacy ledger entries summed alongside bank credits.' },
          { col: 'ubm_locations', use: 'Properties count + lat/lng for the Sites Map. Filter by `status="active"`.' },
          { col: 'ubm_spaces', use: 'Properties tile breakdown — `space_type="unit"` for Units count.' },
          { col: 'ubm_meters', use: 'Properties tile breakdown — Meters count where `status="active"`.' },
          { col: 'ubm_utility_accounts', use: 'Properties tile breakdown — utility customer accounts (CA accounts).' },
          { col: 'ubm_unit_accounts', use: 'Properties tile breakdown — per-unit/sub-meter accounts (no status filter).' },
          { col: 'cross_customer_overrides', use: 'Filters out leaked bills via `is_cross_customer != true`. See Cross-Customer module.' },
        ],
        mysql: [
          { tbl: 'public.site (Postgres bpanalytics)', use: 'One-off backfill source for `ubm_locations.lat/lng` via `scripts/backfill_location_coordinates.py`. Live dashboard does not query Postgres.' },
        ],
        notes: [
          'Total Spend invariant: `kpis.total_spend == spend-comparison.grand_total` (both sum bills.total_amount_due over the same filter).',
          'Sub-stats invariant: Total Spend ≥ Requested ≥ Pulled ≥ Paid for every real customer (3 demo/UAT orgs are exempt).',
          'Standard-UOM usage is pre-computed at sync time on each bill (`_dn_usage_kwh / therms / gallons`) by `services.org_dashboard.usage_normalizer`. Backfill endpoint reprocesses everything idempotently.',
          'Monthly bar-chart series uses `period_from` with fallback to `invoice_date`; rejects pre-2000 dates as data corruption.',
          'Per-commodity vendor + account stats use $setUnion over (utility_accounts ∪ unit_accounts) to avoid double-count.',
          'Every chart card has a 3-dot menu (Download JPG / View Large modal / Download CSV) powered by `<ChartCardMenu>` (html-to-image + Recharts).',
          'PG Postgres is no longer hit on dashboard load; map coords come from `ubm_locations.lat/lng` populated by the one-off backfill.',
        ],
      },
      {
        id: 'org-dashboard-link-status',
        route: '/api/dashboard/org/link-status',
        component: 'backend/services/org_dashboard/routes.py',
        purpose: 'Returns property linkage health: how many properties / accounts / meters are linked vs unlinked. Drives the small "Linkage" pill on the dashboard header.',
        api: [
          { method: 'GET', path: '/api/dashboard/org/link-status?account_id=...' },
        ],
        mongo: [
          { col: 'ubm_locations', use: 'COUNT(active) — total properties.' },
          { col: 'ubm_utility_accounts + ubm_unit_accounts', use: 'COUNT(linked_location_id != null) — accounts mapped to a property.' },
          { col: 'ubm_meters', use: 'COUNT(linked_location_id != null) — meters mapped to a property.' },
        ],
        mysql: [],
        notes: [
          'Surfaces "X unmapped accounts" so ops can act before they show up as UNLINKED in Spend Comparison.',
        ],
      },
      {
        id: 'org-dashboard-spend-comparison',
        route: '/api/dashboard/org/mongo/spend-comparison',
        component: 'backend/services/org_dashboard/mongo_routes.py',
        purpose: 'Per-property spend rollup grouped by service address + commodity. Each row carries lat/lng so the same response feeds the Sites Map.',
        api: [
          { method: 'GET', path: '/api/dashboard/org/mongo/spend-comparison?account_id=...&date_from=&date_to=&state=&supplier=&site=' },
        ],
        mongo: [
          { col: 'bills', use: 'PRIMARY — `$group { _id: { account_number, primary_service_address, commodity, linked_location_id }, spend: $sum(total_amount_due) }`.' },
          { col: 'ubm_locations', use: 'Property metadata join (name, city, state, lat, lng) by `id` ↔ bills.linked_location_id.' },
          { col: 'ubm_utility_accounts + ubm_unit_accounts', use: 'Account → location fallback when bills.linked_location_id is missing.' },
        ],
        mysql: [],
        notes: [
          'Defensive guard: if a bill\'s `linked_location_id` points to a location that is NOT in this account\'s `loc_meta` (cross-customer leak), it is reclassified as UNLINKED — avoids duplicate React keys and prevents leaks into the report.',
          'Total spend uses `bills.total_amount_due` so it reconciles with the KPI Total Spend tile to the cent.',
          '`unmapped_count` / `unmapped_spend` summarise UNLINKED rows so users see the dollar weight of the linkage backlog.',
        ],
      },
      {
        id: 'org-dashboard-kpis',
        route: '/api/dashboard/org/mongo/kpis',
        component: 'backend/services/org_dashboard/mongo_routes.py',
        purpose: 'Top-row KPI tile data: Total Spend (with Requested/Pulled/Paid sub-stats) + Properties (with Units/Meters/Accounts sub-stats).',
        api: [
          { method: 'GET', path: '/api/dashboard/org/mongo/kpis?account_id=...&date_from=&date_to=' },
        ],
        mongo: [
          { col: 'bills', use: 'Total Spend = `$sum(total_amount_due)` filtered by account + date range + non-cross-customer.' },
          { col: 'pm_payment_file_bills', use: 'Requested = `$sum(payment_amount)`, Paid = `$sum(paid_amount)`.' },
          { col: 'pm_bank_statement_lines + pm_funds_pulled', use: 'Pulled = bank credits + legacy ledger.' },
          { col: 'ubm_locations / ubm_spaces / ubm_meters / ubm_utility_accounts / ubm_unit_accounts', use: 'Properties tile breakdown counts.' },
        ],
        mysql: [],
        notes: [
          'Returned shape: `{total_spend, pf_bill_spend (=Requested), paid_bill_spend (=Paid), pulled_amount, properties_count, units_count, meters_count, accounts_count, bill_count}`.',
          'Pulled formula MIRRORS `/api/payments/v2/workspace/dashboard.invoice_summary.funds_pulled.total` so the two pages reconcile.',
          'Date range applies only to Total Spend / Requested / Paid / Pulled. Property-side counts are unfiltered (always reflect "today").',
        ],
      },
      {
        id: 'org-dashboard-commodity-usage',
        route: '/api/dashboard/org/mongo/commodity-usage',
        component: 'backend/services/org_dashboard/mongo_routes.py',
        purpose: 'Drives the 3 commodity hero tiles (Electricity / Gas / Water) and their monthly bar charts + footer "Vendors / Accounts" stat strip.',
        api: [
          { method: 'GET', path: '/api/dashboard/org/mongo/commodity-usage?account_id=...&date_from=&date_to=' },
          { method: 'GET', path: '/api/dashboard/org/mongo/commodity-usage-export?account_id=...&commodity=electricity|gas|water' },
        ],
        mongo: [
          { col: 'bills', use: 'PRIMARY — single `$facet` produces (a) totals: $sum of `_dn_usage_kwh / therms / gallons`, (b) series: per-month rollup, (c) commodity_meta: distinct vendor + account counts via $setUnion.' },
        ],
        mysql: [],
        notes: [
          '`_dn_usage_*` are denorm fields written at bill-sync time by `services.org_dashboard.usage_normalizer.normalize_bill_usage`. Conversion table covers kWh / Therms / CCF / KGAL / m3 / Dekatherms etc. with industry HHV factors.',
          'Demand `kW` is explicitly excluded from energy totals (it is power, not consumption).',
          'POST `/backfill-usage` rewrites the denorm fields for every bill (or one account) — idempotent, ~58ms DB-only for 17.5k bills.',
          'CSV export columns: Sitename, Account, Address, Vendor, Bill Period From, Bill Period To, Issue Date, Usage, UOM.',
        ],
      },
      {
        id: 'org-dashboard-monthly-spend',
        route: '/api/dashboard/org/mongo/monthly-spend',
        component: 'backend/services/org_dashboard/mongo_routes.py',
        purpose: 'Drives the Monthly Spend bar chart (stacked by commodity) + its CSV download.',
        api: [
          { method: 'GET', path: '/api/dashboard/org/mongo/monthly-spend?account_id=...&date_from=&date_to=' },
          { method: 'GET', path: '/api/dashboard/org/mongo/monthly-spend-export?account_id=...' },
        ],
        mongo: [
          { col: 'bills', use: 'PRIMARY — `$group by invoice_date YYYY-MM`, sum `current_bill_amount` bucketed per commodity (electricity / water / gas / trash / sewer).' },
        ],
        mysql: [],
        notes: [
          'CSV export columns: Sitename, Account, Address, Vendor, Bill Period From, Bill Period To, Issue Date, Commodity, Charge, Total Amount Due.',
          'Chart uses `current_bill_amount` (current period charge) NOT `total_amount_due` because users want to see new monthly spend, not cumulative arrears.',
        ],
      },
    ],
  },
];

// ───────────────────────── helpers ─────────────────────────
const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const buildPageId = (sectionId, pageId) => `${sectionId}-${pageId}`;

// ───────────────────────── component ─────────────────────────
export default function DocumentationPage() {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(buildPageId(SECTIONS[0].id, SECTIONS[0].pages[0].id));
  const contentRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map((sec) => {
      const matchSec = sec.title.toLowerCase().includes(q) || (sec.summary || '').toLowerCase().includes(q);
      const pages = sec.pages.filter((p) => {
        const hay = [
          p.route, p.component, p.purpose,
          ...(p.api || []).map((a) => `${a.method} ${a.path}`),
          ...(p.mongo || []).map((m) => `${m.col} ${m.use}`),
          ...(p.mysql || []).map((m) => `${m.tbl} ${m.use}`),
          ...(p.notes || []),
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
      return matchSec || pages.length ? { ...sec, pages: matchSec ? sec.pages : pages } : null;
    }).filter(Boolean);
  }, [query]);

  const scrollTo = (id) => {
    setActiveId(id);
    const el = document.getElementById(`doc-${id}`);
    if (el && contentRef.current) {
      const top = el.getBoundingClientRect().top + contentRef.current.scrollTop - 16;
      contentRef.current.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // Track scroll to update active link
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const onScroll = () => {
      const sections = el.querySelectorAll('[data-anchor]');
      let best = null;
      let bestDelta = Infinity;
      sections.forEach((s) => {
        const delta = Math.abs(s.getBoundingClientRect().top - 100);
        if (delta < bestDelta) { bestDelta = delta; best = s; }
      });
      if (best && best.dataset.anchor !== activeId) {
        setActiveId(best.dataset.anchor);
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [activeId]);

  return (
    <div className="flex flex-col" data-testid="documentation-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-bold">Documentation</h2>
            <p className="text-sm text-muted-foreground">Page → Component → MongoDB → MySQL mapping for the platform.</p>
          </div>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, APIs, collections…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-none bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="doc-search"
          />
        </div>
      </div>

      {/* Body: sticky index + scrollable detail */}
      <div className="grid grid-cols-12 gap-6">
        {/* INDEX */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-3" data-testid="doc-index">
          <nav className="sticky top-4 border border-border rounded-none bg-card max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div className="px-3 py-2 border-b border-border bg-muted/40">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Index</span>
            </div>
            <ul className="py-2">
              {filtered.map((sec) => {
                const Icon = sec.icon || FileText;
                return (
                  <li key={sec.id} className="mb-1">
                    <div className="px-3 py-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-foreground">
                      <Icon className="h-3.5 w-3.5" />
                      {sec.title}
                    </div>
                    <ul>
                      {sec.pages.map((p) => {
                        const id = buildPageId(sec.id, p.id);
                        const isActive = activeId === id;
                        return (
                          <li key={id}>
                            <button
                              onClick={() => scrollTo(id)}
                              data-testid={`doc-link-${id}`}
                              className={`w-full text-left flex items-center gap-1.5 pl-7 pr-3 py-1.5 text-xs transition-colors border-l-2 ${
                                isActive
                                  ? 'border-primary bg-muted text-foreground font-medium'
                                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                              }`}
                            >
                              <ChevronRight className="h-3 w-3 shrink-0" />
                              <span className="truncate">{p.route}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-xs text-muted-foreground text-center">No matches.</li>
              )}
            </ul>
          </nav>
        </aside>

        {/* DETAIL */}
        <main
          ref={contentRef}
          className="col-span-12 md:col-span-9 lg:col-span-9 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2"
          data-testid="doc-detail"
        >
          {filtered.map((sec) => (
            <section key={sec.id} className="mb-10">
              <header className="mb-3">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  {(() => { const Icon = sec.icon || FileText; return <Icon className="h-5 w-5 text-primary" />; })()}
                  {sec.title}
                </h3>
                {sec.summary && <p className="text-sm text-muted-foreground mt-1">{sec.summary}</p>}
              </header>

              <div className="space-y-6">
                {sec.pages.map((p) => {
                  const id = buildPageId(sec.id, p.id);
                  return (
                    <article
                      key={id}
                      id={`doc-${id}`}
                      data-anchor={id}
                      data-testid={`doc-page-${id}`}
                      className="border border-border rounded-none bg-card"
                    >
                      <div className="px-4 py-3 border-b border-border bg-muted/30">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <code className="text-sm font-bold text-primary">{p.route}</code>
                            <div className="text-xs text-muted-foreground mt-0.5 font-mono">{p.component}</div>
                          </div>
                        </div>
                        {p.purpose && <p className="text-sm mt-2">{p.purpose}</p>}
                      </div>

                      <div className="p-4 space-y-4">
                        {/* APIs */}
                        {p.api && p.api.length > 0 && (
                          <DocBlock title="API Endpoints">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left px-2 py-1.5 w-20">Method</th>
                                  <th className="text-left px-2 py-1.5">Path</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.api.map((a, i) => (
                                  <tr key={i} className="border-t border-border">
                                    <td className="px-2 py-1.5">
                                      <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded-none ${methodColor(a.method)}`}>
                                        {a.method}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 font-mono">{a.path}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </DocBlock>
                        )}

                        {/* Mongo */}
                        {p.mongo && p.mongo.length > 0 && (
                          <DocBlock title="MongoDB Collections">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left px-2 py-1.5 w-56">Collection</th>
                                  <th className="text-left px-2 py-1.5">Use</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.mongo.map((m, i) => (
                                  <tr key={i} className="border-t border-border align-top">
                                    <td className="px-2 py-1.5 font-mono text-emerald-700 dark:text-emerald-400">{m.col}</td>
                                    <td className="px-2 py-1.5">{m.use}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </DocBlock>
                        )}

                        {/* MySQL */}
                        {p.mysql && p.mysql.length > 0 && (
                          <DocBlock title="MySQL Source Tables">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left px-2 py-1.5 w-56">Table</th>
                                  <th className="text-left px-2 py-1.5">Use</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.mysql.map((m, i) => (
                                  <tr key={i} className="border-t border-border align-top">
                                    <td className="px-2 py-1.5 font-mono text-amber-700 dark:text-amber-400">{m.tbl}</td>
                                    <td className="px-2 py-1.5">{m.use}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </DocBlock>
                        )}

                        {/* Notes */}
                        {p.notes && p.notes.length > 0 && (
                          <DocBlock title="Notes">
                            <ul className="text-xs space-y-1.5 list-disc pl-5">
                              {p.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                          </DocBlock>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground py-12 text-center">
              No matches for &quot;{query}&quot;.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ───────────────────────── small primitives ─────────────────────────
function DocBlock({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{title}</div>
      <div className="border border-border rounded-none overflow-hidden">{children}</div>
    </div>
  );
}

function methodColor(m) {
  switch (m) {
    case 'GET':    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    case 'POST':   return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'PUT':    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'DELETE': return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
    default:       return 'bg-muted text-muted-foreground';
  }
}
